import { useCallback, useEffect, useRef, useState } from 'react'
import { DndProvider, useDrag, useDrop } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import { useTranslation } from 'react-i18next'
import { MenuAlt4Icon, AdjustmentsIcon, OfficeBuildingIcon } from '@heroicons/react/outline'
import Layout from 'containers/Layout'
import apiGet from 'lib/network/apiGet'
import apiPut from 'lib/network/apiPut'
import notify from 'components/atoms/Notify'
import LoadingIcon from 'components/atoms/LoadingIcon'
import Switch from 'components/atoms/Switch'
import classNames from 'classnames'
import { forEach } from 'lodash'

const ItemTypes = {
  SORT_CRITERION: 'sortCriterion',
  COMPANY_ORDER: 'companyOrder',
  FEATURED_COMPANY_ORDER: 'featuredCompanyOrder',
}

const CRITERION_META = {
  isFeaturedActive: {
    labelKey: 'sortingEngine.criteria.isFeaturedActive.label',
    helperKey: 'sortingEngine.criteria.isFeaturedActive.helper',
  },
  companyPriority: {
    labelKey: 'sortingEngine.criteria.companyPriority.label',
    helperKey: 'sortingEngine.criteria.companyPriority.helper',
  },
  totalBookingsCount: {
    labelKey: 'sortingEngine.criteria.totalBookingsCount.label',
    helperKey: 'sortingEngine.criteria.totalBookingsCount.helper',
  },
  discountSortOrder: {
    labelKey: 'sortingEngine.criteria.discountSortOrder.label',
    helperKey: 'sortingEngine.criteria.discountSortOrder.helper',
  },
  distance: {
    labelKey: 'sortingEngine.criteria.distance.label',
    helperKey: 'sortingEngine.criteria.distance.helper',
  },
  sortOrder: {
    labelKey: 'sortingEngine.criteria.sortOrder.label',
    helperKey: 'sortingEngine.criteria.sortOrder.helper',
  },
  averageReviewsRating: {
    labelKey: 'sortingEngine.criteria.averageReviewsRating.label',
    helperKey: 'sortingEngine.criteria.averageReviewsRating.helper',
  },
}

function isFeaturedActive(company) {
  if (!company?.featured) return false
  const now = new Date()
  const start = company.featuredEffectiveStartDate ? new Date(company.featuredEffectiveStartDate) : null
  const end = company.featuredEffectiveEndDate ? new Date(company.featuredEffectiveEndDate) : null
  if (start && start > now) return false
  if (end && end < now) return false
  return true
}

function getCompanyInitial(name) {
  // eslint-disable-next-line
  const trimmed = (name || '').trim()
  // eslint-disable-next-line
  return trimmed ? trimmed.charAt(0).toUpperCase() : '?'
}

const CompanyLogo = ({ company }) => {
  const [logoFailed, setLogoFailed] = useState(false)
  const initial = getCompanyInitial(company?.name)

  if (company?.logoUrl && !logoFailed) {
    return <img src={company.logoUrl} alt="" className="h-9 w-9 rounded object-contain border border-gray-100 shrink-0" onError={() => setLogoFailed(true)} />
  }

  return (
    <div className="h-9 w-9 rounded shrink-0 flex items-center justify-center border border-gray-200 bg-dark-600/10 text-sm font-semibold text-dark-600 uppercase" aria-hidden>
      {initial}
    </div>
  )
}

const DraggableCriterionRow = ({ criterion, index, moveCriterion, onToggleEnabled, onToggleDirection, t }) => {
  const ref = useRef(null)
  const meta = CRITERION_META[criterion.key] || {}
  const isCompanyPriority = criterion.key === 'companyPriority'
  // eslint-disable-next-line
  const isPinned = PINNED_CRITERION_KEYS.includes(criterion.key)

  const [{ isDragging }, drag] = useDrag({
    type: ItemTypes.SORT_CRITERION,
    item: { index },
    canDrag: !isPinned,
    collect: monitor => ({ isDragging: monitor.isDragging() }),
  })

  const [, drop] = useDrop({
    accept: ItemTypes.SORT_CRITERION,
    canDrop: () => !isPinned,
    hover: (item, monitor) => {
      if (!ref.current || isPinned) return
      const dragIndex = item.index
      const hoverIndex = index
      if (dragIndex === hoverIndex) return
      if (dragIndex < PINNED_CRITERION_COUNT || hoverIndex < PINNED_CRITERION_COUNT) return
      moveCriterion(dragIndex, hoverIndex)
      item.index = hoverIndex
    },
  })

  if (!isPinned) {
    drag(drop(ref))
  }

  return (
    <div
      ref={ref}
      className={classNames(
        'grid grid-cols-[auto_1fr_auto] sm:grid-cols-[auto_auto_1fr_auto] items-center gap-3 sm:gap-4 px-3 sm:px-4 py-3 bg-white border border-gray-200 rounded-lg transition-shadow',
        isDragging ? 'opacity-50 shadow-lg ring-2 ring-pink-200' : 'hover:border-gray-300 hover:shadow-sm',
        isPinned && 'bg-gray-50'
      )}
    >
      {isPinned ? <span className="h-5 w-5 shrink-0" aria-hidden /> : <MenuAlt4Icon className="h-5 w-5 text-gray-400 cursor-move shrink-0" />}
      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gray-100 text-xs font-semibold text-gray-600">{index + 1}</span>
      <div className="min-w-0 col-span-1 sm:col-span-1">
        <p className="text-sm font-medium text-gray-900">{t(meta.labelKey || criterion.key)}</p>
        <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{t(meta.helperKey || '')}</p>
      </div>
      {/* Asc/Desc direction toggle hidden — direction values still sent on save
      {isCompanyPriority ? (
        <span className="shrink-0 px-3 py-1.5 text-xs font-medium text-gray-500 border border-gray-200 rounded-md bg-gray-50 whitespace-nowrap">
          {t('sortingEngine.listOrder')}
        </span>
      ) : (
        <button
          type="button"
          onClick={() => onToggleDirection(index)}
          className="shrink-0 px-3 py-1.5 text-xs font-medium border border-gray-300 rounded-md hover:bg-gray-50 whitespace-nowrap"
        >
          {criterion.direction === 'desc' ? t('sortingEngine.desc') : t('sortingEngine.asc')}
        </button>
      )}
      */}
      {isCompanyPriority ? <Switch value onChange={() => {}} disabled /> : <Switch value={criterion.enabled} onChange={checked => onToggleEnabled(index, checked)} />}
    </div>
  )
}

const DraggableCompanyRow = ({ company, index, moveCompany, t }) => {
  const ref = useRef(null)
  const featured = isFeaturedActive(company)

  const [{ isDragging }, drag] = useDrag({
    type: ItemTypes.COMPANY_ORDER,
    item: { index },
    collect: monitor => ({ isDragging: monitor.isDragging() }),
  })

  const [, drop] = useDrop({
    accept: ItemTypes.COMPANY_ORDER,
    hover: (item, monitor) => {
      if (!ref.current) return
      const dragIndex = item.index
      const hoverIndex = index
      if (dragIndex === hoverIndex) return
      moveCompany(dragIndex, hoverIndex)
      item.index = hoverIndex
    },
  })

  drag(drop(ref))

  return (
    <div
      ref={ref}
      className={classNames(
        'flex items-center gap-3 px-3 sm:px-4 py-2.5 bg-white border border-gray-200 rounded-lg transition-shadow',
        isDragging ? 'opacity-50 shadow-lg ring-2 ring-pink-200' : 'hover:border-gray-300 hover:shadow-sm'
      )}
    >
      <MenuAlt4Icon className="h-4 w-4 text-gray-400 cursor-move shrink-0" />
      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded bg-gray-100 text-[11px] font-semibold text-gray-500">{index + 1}</span>
      <CompanyLogo company={company} />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-900 truncate">{company.name}</p>
      </div>
      {featured && <span className="shrink-0 px-2 py-0.5 text-xs font-medium rounded-full bg-pink-100 text-pink-700">{t('sortingEngine.featuredBadge')}</span>}
    </div>
  )
}

const DraggableFeaturedCompanyRow = ({ company, index, moveCompany, t }) => {
  const ref = useRef(null)

  const [{ isDragging }, drag] = useDrag({
    type: ItemTypes.FEATURED_COMPANY_ORDER,
    item: { index },
    collect: monitor => ({ isDragging: monitor.isDragging() }),
  })

  const [, drop] = useDrop({
    accept: ItemTypes.FEATURED_COMPANY_ORDER,
    hover: (item, monitor) => {
      if (!ref.current) return
      const dragIndex = item.index
      const hoverIndex = index
      if (dragIndex === hoverIndex) return
      moveCompany(dragIndex, hoverIndex)
      item.index = hoverIndex
    },
  })

  drag(drop(ref))

  const carsCount = company?.featuredCarsCount ?? 0

  return (
    <div
      ref={ref}
      className={classNames(
        'flex items-center gap-3 px-3 sm:px-4 py-2.5 bg-white border border-gray-200 rounded-lg transition-shadow',
        isDragging ? 'opacity-50 shadow-lg ring-2 ring-pink-200' : 'hover:border-gray-300 hover:shadow-sm'
      )}
    >
      <MenuAlt4Icon className="h-4 w-4 text-gray-400 cursor-move shrink-0" />
      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded bg-gray-100 text-[11px] font-semibold text-gray-500">{index + 1}</span>
      <CompanyLogo company={company} />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-900 truncate">{company.name}</p>
      </div>
      <span className="shrink-0 px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-600">
        {t('sortingEngine.featuredSorting.carsCount', { count: carsCount, defaultValue: '{{count}} cars' })}
      </span>
    </div>
  )
}

const StrategyCard = ({ selected, onSelect, title, helper, name }) => (
  <button
    type="button"
    onClick={onSelect}
    className={classNames(
      'w-full text-left rounded-lg border-2 p-4 sm:p-5 transition-all',
      selected ? 'border-dark-600 bg-dark-600/[0.09] shadow-sm ring-1 ring-dark-600/25' : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
    )}
  >
    <div className="flex items-start gap-3">
      <input type="radio" name={name} checked={selected} readOnly className="mt-1 h-4 w-4 border-gray-300 accent-dark-600 focus:ring-dark-500" />
      <div>
        <p className="text-sm font-semibold text-gray-900">{title}</p>
        <p className="text-xs text-gray-500 mt-1 leading-relaxed">{helper}</p>
      </div>
    </div>
  </button>
)

const PINNED_CRITERION_KEYS = ['companyPriority', 'isFeaturedActive']
const PINNED_CRITERION_COUNT = PINNED_CRITERION_KEYS.length

function enforcePinnedCriteriaOrder(criteriaList) {
  const list = criteriaList || []
  const byKey = new Map(list.map(c => [c.key, c]))
  const pinned = PINNED_CRITERION_KEYS.map(key => byKey.get(key)).filter(Boolean)
  // eslint-disable-next-line
  const rest = list.filter(c => !PINNED_CRITERION_KEYS.includes(c.key))
  return [...pinned, ...rest]
}
const DEFAULT_CRITERIA = [
  { key: 'companyPriority', direction: 'asc', enabled: true },
  { key: 'isFeaturedActive', direction: 'desc', enabled: true },
  { key: 'totalBookingsCount', direction: 'desc', enabled: true },
  { key: 'discountSortOrder', direction: 'asc', enabled: true },
  { key: 'distance', direction: 'asc', enabled: true },
  { key: 'sortOrder', direction: 'asc', enabled: true },
  { key: 'averageReviewsRating', direction: 'desc', enabled: false },
]

const VALID_CRITERION_KEYS = new Set(DEFAULT_CRITERIA.map(c => c.key))

function mergeCriteriaWithDefaults(criteriaList) {
  const list = criteriaList || []
  if (list.length === 0) {
    return DEFAULT_CRITERIA.map(c => (c.key === 'companyPriority' ? { ...c, enabled: true } : { ...c }))
  }

  const merged = list.filter(c => c?.key && VALID_CRITERION_KEYS.has(c.key))
  const present = new Set(merged.map(c => c.key))

  forEach(DEFAULT_CRITERIA, def => {
    if (!present.has(def.key)) {
      merged.push(def.key === 'companyPriority' ? { ...def, enabled: true } : { ...def })
      present.add(def.key)
    }
  })

  return enforcePinnedCriteriaOrder(merged.map(c => (c.key === 'companyPriority' ? { ...c, enabled: true } : c)))
}

function ensureCompanyPriorityEnabled(criteriaList) {
  return (criteriaList || []).map(c => (c.key === 'companyPriority' ? { ...c, enabled: true } : c))
}

const SortingEngine = () => {
  const { t } = useTranslation()
  const [activeTab, setActiveTab] = useState('sorting')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [strategy, setStrategy] = useState('interleaved')
  const [criteria, setCriteria] = useState([])
  const [companies, setCompanies] = useState([])
  const [featuredCompanies, setFeaturedCompanies] = useState([])
  const [featuredLoading, setFeaturedLoading] = useState(false)
  const [featuredLoaded, setFeaturedLoaded] = useState(false)
  const [featuredSaving, setFeaturedSaving] = useState(false)

  const loadConfig = useCallback(async () => {
    setLoading(true)
    try {
      const response = await apiGet('sorting-engine')
      if (response?.success && response?.data) {
        setStrategy(response.data.strategy || 'interleaved')
        setCriteria(mergeCriteriaWithDefaults(response.data.criteria))
        setCompanies(response.data.companyOrder || [])
      }
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadConfig()
  }, [loadConfig])

  const moveCriterion = (dragIndex, hoverIndex) => {
    if (dragIndex < PINNED_CRITERION_COUNT || hoverIndex < PINNED_CRITERION_COUNT) return
    setCriteria(prev => {
      const next = [...prev]
      const [removed] = next.splice(dragIndex, 1)
      next.splice(hoverIndex, 0, removed)
      return enforcePinnedCriteriaOrder(next)
    })
  }

  const moveCompany = (dragIndex, hoverIndex) => {
    setCompanies(prev => {
      const next = [...prev]
      const [removed] = next.splice(dragIndex, 1)
      next.splice(hoverIndex, 0, removed)
      return next
    })
  }

  const handleToggleEnabled = (index, enabled) => {
    setCriteria(prev =>
      prev.map((c, i) => {
        if (i !== index) return c
        if (c.key === 'companyPriority') return { ...c, enabled: true }
        return { ...c, enabled }
      })
    )
  }

  const handleToggleDirection = index => {
    setCriteria(prev => prev.map((c, i) => (i === index ? { ...c, direction: c.direction === 'desc' ? 'asc' : 'desc' } : c)))
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      const payload = {
        strategy,
        criteria: mergeCriteriaWithDefaults(criteria),
        companyOrder: companies.map(c => c._id),
      }
      const response = await apiPut('sorting-engine', payload)
      if (response?.success) {
        notify.success(t('sortingEngine.saveSuccess'))
        if (response.data) {
          setStrategy(response.data.strategy)
          setCriteria(ensureCompanyPriorityEnabled(enforcePinnedCriteriaOrder(response.data.criteria)))
          if (response.data.companyOrder?.length) {
            setCompanies(response.data.companyOrder)
          }
        }
      }
    } finally {
      setSaving(false)
    }
  }

  const loadFeatured = useCallback(async () => {
    setFeaturedLoading(true)
    try {
      const response = await apiGet('sorting-engine/featured-companies')
      if (response?.success) {
        setFeaturedCompanies(response.data || [])
        setFeaturedLoaded(true)
      }
    } finally {
      setFeaturedLoading(false)
    }
  }, [])

  useEffect(() => {
    if (activeTab === 'featured' && !featuredLoaded) {
      loadFeatured()
    }
  }, [activeTab, featuredLoaded, loadFeatured])

  const moveFeaturedCompany = (dragIndex, hoverIndex) => {
    setFeaturedCompanies(prev => {
      const next = [...prev]
      const [removed] = next.splice(dragIndex, 1)
      next.splice(hoverIndex, 0, removed)
      return next
    })
  }

  const handleSaveFeatured = async () => {
    setFeaturedSaving(true)
    try {
      const payload = {
        featuredCompanyOrder: featuredCompanies.map(c => c._id),
      }
      const response = await apiPut('sorting-engine/featured-companies', payload)
      if (response?.success) {
        notify.success(t('sortingEngine.saveSuccess'))
        if (response.data) {
          setFeaturedCompanies(response.data)
        }
      }
    } finally {
      setFeaturedSaving(false)
    }
  }

  return (
    <Layout>
      <div className="px-4 sm:px-6 lg:px-8 py-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-semibold text-gray-900">{t('sortingEngine.title')}</h1>
          <p className="mt-1 text-sm text-gray-500 max-w-3xl">{t('sortingEngine.subtitle')}</p>
        </div>

        {/* Tabs */}
        <div className="mb-6 border-b border-gray-200">
          <nav className="-mb-px flex gap-6">
            {[
              { id: 'sorting', label: t('sortingEngine.tabs.sorting', { defaultValue: 'Sorting Engine' }) },
              { id: 'featured', label: t('sortingEngine.tabs.featured', { defaultValue: 'Featured Sorting' }) },
            ].map(tab => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={classNames(
                  'whitespace-nowrap border-b-2 pb-3 pt-1 text-sm font-medium transition-colors',
                  activeTab === tab.id ? 'border-dark-600 text-dark-600' : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                )}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        <DndProvider backend={HTML5Backend}>
          {activeTab === 'sorting' &&
            (loading ? (
              <div className="flex justify-center py-24">
                <LoadingIcon />
              </div>
            ) : (
              <div className="space-y-6">
                {/* Strategy — full-width row */}
                <section className="bg-white border border-gray-200 rounded-xl p-5 sm:p-6">
                  <h2 className="text-base font-semibold text-gray-900">{t('sortingEngine.strategyTitle')}</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                    <StrategyCard
                      name="strategy"
                      selected={strategy === 'interleaved'}
                      onSelect={() => setStrategy('interleaved')}
                      title={t('sortingEngine.strategyInterleaved')}
                      helper={t('sortingEngine.strategyInterleavedHelper')}
                    />
                    <StrategyCard
                      name="strategy"
                      selected={strategy === 'grouped'}
                      onSelect={() => setStrategy('grouped')}
                      title={t('sortingEngine.strategyGrouped')}
                      helper={t('sortingEngine.strategyGroupedHelper')}
                    />
                  </div>
                </section>

                {/* Two-column main area */}
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 items-start">
                  {/* Sort criteria */}
                  <section className="bg-gray-50 border border-gray-200 rounded-xl flex flex-col min-h-[420px] max-h-[calc(100vh-280px)]">
                    <div className="px-5 py-4 border-b border-gray-200 bg-white rounded-t-xl shrink-0">
                      <div className="flex items-center gap-2">
                        <AdjustmentsIcon className="h-5 w-5 text-gray-500" />
                        <div>
                          <h2 className="text-base font-semibold text-gray-900">{t('sortingEngine.criteriaTitle')}</h2>
                          <p className="text-xs text-gray-500 mt-0.5">{t('sortingEngine.criteriaSubtitle')}</p>
                        </div>
                      </div>
                      <div className="hidden lg:grid grid-cols-[auto_auto_1fr_auto] gap-4 mt-3 px-3 text-[11px] font-medium uppercase tracking-wide text-gray-400">
                        <span className="w-5" />
                        <span className="w-7 text-center">#</span>
                        <span>{t('sortingEngine.criteriaTitle')}</span>
                        {/* <span>{t('sortingEngine.desc')}</span> */}
                        <span>On</span>
                      </div>
                    </div>
                    <div className="flex-1 overflow-y-auto p-4 space-y-2">
                      {criteria.map((criterion, index) => (
                        <DraggableCriterionRow
                          key={criterion.key}
                          criterion={criterion}
                          index={index}
                          moveCriterion={moveCriterion}
                          onToggleEnabled={handleToggleEnabled}
                          onToggleDirection={handleToggleDirection}
                          t={t}
                        />
                      ))}
                    </div>
                  </section>

                  {/* Company priority */}
                  <section className="bg-gray-50 border border-gray-200 rounded-xl flex flex-col min-h-[420px] max-h-[calc(100vh-280px)]">
                    <div className="px-5 py-4 border-b border-gray-200 bg-white rounded-t-xl shrink-0">
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-2 min-w-0">
                          <OfficeBuildingIcon className="h-5 w-5 text-gray-500 shrink-0" />
                          <div className="min-w-0">
                            <h2 className="text-base font-semibold text-gray-900">{t('sortingEngine.companyPriorityTitle')}</h2>
                            <p className="text-xs text-gray-500 mt-0.5 truncate">{t('sortingEngine.companyPrioritySubtitle')}</p>
                          </div>
                        </div>
                        <span className="shrink-0 text-xs font-medium text-gray-500 bg-gray-100 px-2.5 py-1 rounded-full">
                          {companies.length} {t('sortingEngine.companiesCount', { defaultValue: 'companies' })}
                        </span>
                      </div>
                    </div>
                    <div className="flex-1 overflow-y-auto p-4 space-y-1.5">
                      {companies.length === 0 ? (
                        <p className="text-sm text-gray-500 px-1 py-4">{t('sortingEngine.noActiveCompanies')}</p>
                      ) : (
                        companies.map((company, index) => <DraggableCompanyRow key={company._id} company={company} index={index} moveCompany={moveCompany} t={t} />)
                      )}
                    </div>
                  </section>
                </div>

                {/* Bottom save bar — visible on scroll */}
                <div className="sticky bottom-0 z-10 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 py-3 bg-white/95 backdrop-blur border-t border-gray-200 flex justify-end">
                  <button
                    type="button"
                    onClick={handleSave}
                    disabled={saving}
                    className="inline-flex items-center px-5 py-2.5 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-dark-600 hover:bg-dark-700 disabled:opacity-50"
                  >
                    {saving ? t('sortingEngine.saving') : t('sortingEngine.save')}
                  </button>
                </div>
              </div>
            ))}

          {activeTab === 'featured' &&
            (featuredLoading ? (
              <div className="flex justify-center py-24">
                <LoadingIcon />
              </div>
            ) : (
              <div className="space-y-6">
                <section className="bg-gray-50 border border-gray-200 rounded-xl flex flex-col min-h-[420px] max-h-[calc(100vh-280px)]">
                  <div className="px-5 py-4 border-b border-gray-200 bg-white rounded-t-xl shrink-0">
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2 min-w-0">
                        <OfficeBuildingIcon className="h-5 w-5 text-gray-500 shrink-0" />
                        <div className="min-w-0">
                          <h2 className="text-base font-semibold text-gray-900">{t('sortingEngine.featuredSorting.title', { defaultValue: 'Featured company order' })}</h2>
                          <p className="text-xs text-gray-500 mt-0.5 truncate">
                            {t('sortingEngine.featuredSorting.subtitle', { defaultValue: 'Drag to set which featured company shows first.' })}
                          </p>
                        </div>
                      </div>
                      <span className="shrink-0 text-xs font-medium text-gray-500 bg-gray-100 px-2.5 py-1 rounded-full">
                        {featuredCompanies.length} {t('sortingEngine.companiesCount', { defaultValue: 'companies' })}
                      </span>
                    </div>
                  </div>
                  <div className="flex-1 overflow-y-auto p-4 space-y-1.5">
                    {featuredCompanies.length === 0 ? (
                      <p className="text-sm text-gray-500 px-1 py-4">
                        {t('sortingEngine.featuredSorting.empty', {
                          defaultValue: 'No active featured companies. Mark companies as featured (within their effective dates) to arrange them here.',
                        })}
                      </p>
                    ) : (
                      featuredCompanies.map((company, index) => (
                        <DraggableFeaturedCompanyRow key={company._id} company={company} index={index} moveCompany={moveFeaturedCompany} t={t} />
                      ))
                    )}
                  </div>
                </section>

                {/* Bottom save bar — visible on scroll */}
                <div className="sticky bottom-0 z-10 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 py-3 bg-white/95 backdrop-blur border-t border-gray-200 flex justify-end">
                  <button
                    type="button"
                    onClick={handleSaveFeatured}
                    disabled={featuredSaving || featuredCompanies.length === 0}
                    className="inline-flex items-center px-5 py-2.5 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-dark-600 hover:bg-dark-700 disabled:opacity-50"
                  >
                    {featuredSaving ? t('sortingEngine.saving') : t('sortingEngine.save')}
                  </button>
                </div>
              </div>
            ))}
        </DndProvider>
      </div>
    </Layout>
  )
}

export default SortingEngine
